import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronDown, Check } from 'lucide-react';
import { LlmColumn, getDimensionColumns, getMeasureColumns } from './tableToChartData';

interface ChartConfigDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    columns: LlmColumn[];
    selectedDimension: string;
    selectedSecondaryDimension?: string;
    selectedMeasures: string[];
    chartType: string;
    onApply: (chartType: string, dimension: string, measures: string[], secondaryDimension?: string) => void;
}

const ChartConfigDrawer: React.FC<ChartConfigDrawerProps> = ({
    isOpen,
    onClose,
    columns,
    selectedDimension,
    selectedSecondaryDimension,
    selectedMeasures,
    chartType,
    onApply
}) => {
    const [selectedChartType, setSelectedChartType] = useState(chartType);
    const [dimension, setDimension] = useState(selectedDimension);
    const [secondaryDimension, setSecondaryDimension] = useState<string>(selectedSecondaryDimension || '');
    const [measures, setMeasures] = useState<string[]>(selectedMeasures);
    const [chartTypeDropdownOpen, setChartTypeDropdownOpen] = useState(false);
    const [dimensionDropdownOpen, setDimensionDropdownOpen] = useState(false);
    const [secondaryDimensionDropdownOpen, setSecondaryDimensionDropdownOpen] = useState(false);
    const [measuresDropdownOpen, setMeasuresDropdownOpen] = useState(false);
    const dimensionRef = useRef<HTMLDivElement>(null);
    const secondaryDimensionRef = useRef<HTMLDivElement>(null);
    const measuresRef = useRef<HTMLDivElement>(null);
    const chartTypeRef = useRef<HTMLDivElement>(null);

    // Check if chart type supports grouping (secondary dimension)
    const supportsGrouping = selectedChartType === 'barchart' || selectedChartType === 'columnchart';

    const dimensionColumns = getDimensionColumns(columns || []);
    const measureColumns = getMeasureColumns(columns || []);

    useEffect(() => {
        if (isOpen) {
            setSelectedChartType(chartType);
            setDimension(selectedDimension);
            setSecondaryDimension(selectedSecondaryDimension || '');
            if (chartType === 'piechart' && selectedMeasures.length > 1) {
                setMeasures([selectedMeasures[0]]);
            } else {
                setMeasures(selectedMeasures);
            }
        }
    }, [isOpen, chartType, selectedDimension, selectedSecondaryDimension, selectedMeasures]);

    useEffect(() => {
        if (selectedChartType === 'piechart' && measures.length > 1) {
            setMeasures([measures[0]]);
        }
        // Clear secondary dimension when switching to unsupported chart types
        if (selectedChartType !== 'barchart' && selectedChartType !== 'columnchart') {
            setSecondaryDimension('');
        }
    }, [selectedChartType]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dimensionRef.current && !dimensionRef.current.contains(event.target as Node)) {
                setDimensionDropdownOpen(false);
            }
            if (secondaryDimensionRef.current && !secondaryDimensionRef.current.contains(event.target as Node)) {
                setSecondaryDimensionDropdownOpen(false);
            }
            if (measuresRef.current && !measuresRef.current.contains(event.target as Node)) {
                setMeasuresDropdownOpen(false);
            }
            if (chartTypeRef.current && !chartTypeRef.current.contains(event.target as Node)) {
                setChartTypeDropdownOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [isOpen]);

    const handleDimensionSelect = (columnName: string) => {
        setDimension(columnName);
        setDimensionDropdownOpen(false);
        // Clear secondary dimension if it's the same as new primary
        if (secondaryDimension === columnName) {
            setSecondaryDimension('');
        }
    };

    const handleSecondaryDimensionSelect = (columnName: string) => {
        setSecondaryDimension(columnName);
        setSecondaryDimensionDropdownOpen(false);
        // When secondary dimension is selected, restrict to single measure
        if (columnName && measures.length > 1) {
            setMeasures([measures[0]]);
        }
    };

    const handleMeasureToggle = (columnName: string) => {
        setMeasures(prev => {
            if (prev.includes(columnName)) {
                return prev.filter(m => m !== columnName);
            } else {
                // Restrict to single measure for pie charts or when secondary dimension is set
                if (selectedChartType === 'piechart' || secondaryDimension) {
                    return [columnName];
                }
                return [...prev, columnName];
            }
        });
    };

    const handleApply = () => {
        if (dimension && measures.length > 0) {
            onApply(selectedChartType, dimension, measures, secondaryDimension || undefined);
            onClose();
        }
    };

    const getColumnDisplayName = (columnName: string): string => {
        const col = columns.find(c => c.name === columnName);
        return col?.displayName || columnName;
    };

    const getChartTypeDescription = (type: string): string => {
        switch (type) {
            case 'barchart':
                return 'Bar Chart';
            case 'columnchart':
                return 'Column Chart';
            case 'linechart':
                return measures.length > 1 ? 'Multi-Line Chart' : 'Line Chart';
            case 'piechart':
                return 'Pie Chart';
            default:
                return 'Chart';
        }
    };

    const chartTypes = [
        { value: 'barchart', label: 'Bar Chart' },
        { value: 'columnchart', label: 'Column Chart' },
        { value: 'linechart', label: 'Line Chart' },
        { value: 'piechart', label: 'Pie Chart' }
    ];

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    if (!isOpen) {
        return null;
    }

    const drawerContent = (
        <div className="fixed inset-0 z-[9999] flex justify-end">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black bg-opacity-30"
                onClick={onClose}
            />

            {/* Drawer */}
            <div className="w-full md:w-[400px] h-screen bg-white flex flex-col shadow-lg relative z-[10000]">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">
                        Configure Chart
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                        title="Close drawer"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 p-4 overflow-y-auto">
                    {/* Chart Type Selection */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Chart Type
                        </label>
                        <div className="relative" ref={chartTypeRef}>
                            <button
                                type="button"
                                onClick={() => setChartTypeDropdownOpen(!chartTypeDropdownOpen)}
                                className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-300 rounded-xl text-left hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                title={getChartTypeDescription(selectedChartType)}
                            >
                                <span className="text-gray-900">
                                    {getChartTypeDescription(selectedChartType)}
                                </span>
                                <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${chartTypeDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {chartTypeDropdownOpen && (
                                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-10 max-h-60 overflow-y-auto">
                                    {chartTypes.map((type) => (
                                        <button
                                            key={type.value}
                                            type="button"
                                            onClick={() => {
                                                setSelectedChartType(type.value);
                                                setChartTypeDropdownOpen(false);
                                            }}
                                            className={`w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 ${selectedChartType === type.value ? 'bg-blue-50 text-blue-700' : 'text-gray-900'
                                                }`}
                                            title={`Select ${type.label}`}
                                        >
                                            <span>{type.label}</span>
                                            {selectedChartType === type.value && <Check className="w-4 h-4 text-blue-600" />}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Dimension Selection */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            X-Axis (Dimension)
                        </label>
                        <div className="relative" ref={dimensionRef}>
                            <button
                                type="button"
                                onClick={() => setDimensionDropdownOpen(!dimensionDropdownOpen)}
                                className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-300 rounded-xl text-left hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <span className="text-gray-900">
                                    {dimension ? getColumnDisplayName(dimension) : 'Select dimension column'}
                                </span>
                                <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${dimensionDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {dimensionDropdownOpen && (
                                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-10 max-h-60 overflow-y-auto">
                                    {dimensionColumns.map((col) => (
                                        <button
                                            key={col.name}
                                            type="button"
                                            onClick={() => handleDimensionSelect(col.name)}
                                            className={`w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 ${dimension === col.name ? 'bg-blue-50 text-blue-700' : 'text-gray-900'
                                                }`}
                                        >
                                            <span>{col.displayName}</span>
                                            {dimension === col.name && <Check className="w-4 h-4 text-blue-600" />}
                                        </button>
                                    ))}
                                    {dimensionColumns.length === 0 && (
                                        <div className="px-4 py-3 text-gray-500 text-sm">
                                            No dimension columns available
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Secondary Dimension Selection - Only for bar/column charts */}
                    {supportsGrouping && dimensionColumns.length > 1 && (
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Stack By (Optional)
                                <span className="text-gray-400 font-normal ml-1">- Creates stacked bars</span>
                            </label>
                            <div className="relative" ref={secondaryDimensionRef}>
                                <button
                                    type="button"
                                    onClick={() => setSecondaryDimensionDropdownOpen(!secondaryDimensionDropdownOpen)}
                                    className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-300 rounded-xl text-left hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <span className={secondaryDimension ? 'text-gray-900' : 'text-gray-400'}>
                                        {secondaryDimension ? getColumnDisplayName(secondaryDimension) : 'None (single series)'}
                                    </span>
                                    <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${secondaryDimensionDropdownOpen ? 'rotate-180' : ''}`} />
                                </button>

                                {secondaryDimensionDropdownOpen && (
                                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-10 max-h-60 overflow-y-auto">
                                        {/* Clear option */}
                                        <button
                                            type="button"
                                            onClick={() => handleSecondaryDimensionSelect('')}
                                            className={`w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 ${!secondaryDimension ? 'bg-blue-50 text-blue-700' : 'text-gray-500 italic'}`}
                                        >
                                            <span>None (single series)</span>
                                            {!secondaryDimension && <Check className="w-4 h-4 text-blue-600" />}
                                        </button>
                                        {/* Dimension columns (excluding primary) */}
                                        {dimensionColumns
                                            .filter(col => col.name !== dimension)
                                            .map((col) => (
                                                <button
                                                    key={col.name}
                                                    type="button"
                                                    onClick={() => handleSecondaryDimensionSelect(col.name)}
                                                    className={`w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 ${secondaryDimension === col.name ? 'bg-blue-50 text-blue-700' : 'text-gray-900'}`}
                                                >
                                                    <span>{col.displayName}</span>
                                                    {secondaryDimension === col.name && <Check className="w-4 h-4 text-blue-600" />}
                                                </button>
                                            ))}
                                        {dimensionColumns.filter(col => col.name !== dimension).length === 0 && (
                                            <div className="px-4 py-3 text-gray-500 text-sm">
                                                No other dimension columns available
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            {secondaryDimension && (
                                <p className="mt-2 text-xs text-gray-500">
                                    Note: With grouping enabled, only one measure can be selected
                                </p>
                            )}
                        </div>
                    )}

                    {/* Measures Selection */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Y-Axis (Values) {(selectedChartType === 'piechart' || secondaryDimension) ? '- Select one' : '- Select one or more'}
                        </label>
                        <div className="relative" ref={measuresRef}>
                            <button
                                type="button"
                                onClick={() => setMeasuresDropdownOpen(!measuresDropdownOpen)}
                                className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-300 rounded-xl text-left hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <span className="text-gray-900 truncate">
                                    {measures.length > 0
                                        ? measures.map(m => getColumnDisplayName(m)).join(', ')
                                        : 'Select measure columns'}
                                </span>
                                <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${measuresDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {measuresDropdownOpen && (
                                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-10 max-h-60 overflow-y-auto">
                                    {measureColumns.map((col) => {
                                        const isSelected = measures.includes(col.name);
                                        const isDisabled = selectedChartType === 'piechart' && !isSelected && measures.length >= 1;
                                        return (
                                            <button
                                                key={col.name}
                                                type="button"
                                                onClick={() => !isDisabled && handleMeasureToggle(col.name)}
                                                disabled={isDisabled}
                                                className={`w-full flex items-center justify-between px-4 py-3 text-left ${isDisabled
                                                    ? 'opacity-50 cursor-not-allowed text-gray-400'
                                                    : isSelected
                                                        ? 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                                                        : 'hover:bg-gray-50 text-gray-900'
                                                    }`}
                                            >
                                                <span>{col.displayName}</span>
                                                {isSelected && <Check className="w-4 h-4 text-blue-600" />}
                                            </button>
                                        );
                                    })}
                                    {measureColumns.length === 0 && (
                                        <div className="px-4 py-3 text-gray-500 text-sm">
                                            No measure columns available
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Selected measures pills */}
                        {measures.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-3">
                                {measures.map((m) => (
                                    <span
                                        key={m}
                                        className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                                    >
                                        {getColumnDisplayName(m)}
                                        {selectedChartType !== 'piechart' && (
                                            <button
                                                type="button"
                                                onClick={() => handleMeasureToggle(m)}
                                                className="hover:text-blue-900"
                                                title={`Remove ${getColumnDisplayName(m)}`}
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        )}
                                    </span>
                                ))}
                            </div>
                        )}
                        {selectedChartType === 'piechart' && measures.length > 0 && (
                            <p className="mt-2 text-xs text-gray-500">
                                Pie charts support only one measure
                            </p>
                        )}
                    </div>

                </div>

                {/* Footer */}
                <div className="px-4 py-3 border-t border-gray-200 flex-shrink-0">
                    <div className="flex items-center justify-end space-x-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-tertiary-text border border-gray-300 rounded-full hover:bg-gray-50 transition-colors text-ui"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleApply}
                            disabled={!dimension || measures.length === 0 || (selectedChartType === 'piechart' && measures.length !== 1)}
                            className="px-6 py-2 bg-primary text-white rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-ui"
                        >
                            Apply
                        </button>
                    </div>
                </div>
            </div>
        </div >
    );

    if (typeof document !== 'undefined') {
        return createPortal(drawerContent, document.body);
    }

    return null;
};

export default ChartConfigDrawer;
